// Calendar event positioning utilities for TradeReady
// Calculates pixel positions for time-based event rendering

/**
 * Calculate top position and height for an event based on start/end times
 * Assumes 60px per hour in a 24-hour day view
 * 
 * @param startTime - ISO string or Date object for event start
 * @param endTime - ISO string or Date object for event end
 * @param pixelsPerHour - Height in pixels for one hour (default: 60)
 * @returns Object with top position and height in pixels
 */
export function calculateEventPosition(
  startTime: string | Date,
  endTime: string | Date,
  pixelsPerHour: number = 60
): { top: number; height: number } {
  const start = new Date(startTime);
  const end = new Date(endTime);

  // Calculate hours from midnight (0-24)
  const startHours = start.getHours() + start.getMinutes() / 60;
  const endHours = end.getHours() + end.getMinutes() / 60;

  // Calculate pixel positions
  const top = startHours * pixelsPerHour;
  const duration = endHours - startHours;
  const height = Math.max(duration * pixelsPerHour, 30); // Minimum 30px height

  return { top, height };
}

/**
 * Calculate top position for an event
 * @param startTime - ISO string or Date object for event start
 * @param pixelsPerHour - Height in pixels for one hour (default: 60)
 * @returns Top position in pixels from midnight
 */
export function calculateEventTop(
  startTime: string | Date,
  pixelsPerHour: number = 60
): number {
  const start = new Date(startTime);
  const startHours = start.getHours() + start.getMinutes() / 60;
  return startHours * pixelsPerHour;
}

/**
 * Calculate height for an event
 * @param startTime - ISO string or Date object for event start
 * @param endTime - ISO string or Date object for event end
 * @param pixelsPerHour - Height in pixels for one hour (default: 60)
 * @returns Height in pixels
 */
export function calculateEventHeight(
  startTime: string | Date,
  endTime: string | Date,
  pixelsPerHour: number = 60
): number {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const startHours = start.getHours() + start.getMinutes() / 60;
  const endHours = end.getHours() + end.getMinutes() / 60;
  const duration = endHours - startHours;
  return Math.max(duration * pixelsPerHour, 30); // Minimum 30px height
}

/**
 * Generate hour markers for a day view
 * @param startHour - First hour to display (default: 0 for midnight)
 * @param endHour - Last hour to display (default: 24)
 * @returns Array of hour labels and positions
 */
export function generateHourMarkers(
  startHour: number = 0,
  endHour: number = 24
): Array<{ hour: number; label: string; position: number }> {
  const markers = [];
  
  for (let hour = startHour; hour < endHour; hour++) {
    markers.push({
      hour,
      label: formatHour(hour),
      position: hour * 60, // 60px per hour
    });
  }

  return markers;
}

/**
 * Format hour for display (12-hour format)
 */
function formatHour(hour: number): string {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
}
